import { FieldValue, Timestamp, DocumentReference } from 'firebase/firestore'

// Common Enums
export enum EntityType {
  Post = 'Post',
  Comment = 'Comment',
  Reply = 'Reply',
  Question = 'Question',
  Answer = 'Answer',
}

export enum AuthorType {
  DisplayName = 'displayName',
  PenName = 'penName',
}

export enum ReportableEntity {
  User = 'User',
  Post = 'Post',
  Comment = 'Comment',
  Reply = 'Reply',
}

export enum ReportReason {
  InappropriateContent = 'inappropriate_content',
  Spam = 'spam',
  Harassment = 'harassment',
  Other = 'other',
}

export enum AppealReason {
  WrongfulReport = 'wrongful_report',
  ContentMisjudged = 'content_misjudged',
  PolicyMisapplied = 'policy_misapplied',
  Other = 'other',
}

export enum AppealRejectionReason {
  InappropriateContent = 'InappropriateContent',
  Spam = 'Spam',
  Harassment = 'Harassment',
  Other = 'Other',
}

// Generic Base Interfaces
interface BaseEntity<T extends FieldValue | Timestamp> {
  id: string
  userId: string
  created_at: T
  updated_at: T | null
  isVisible: boolean
}

export type BaseEntityWrite = BaseEntity<FieldValue>
export type BaseEntityRead = BaseEntity<Timestamp>

interface UserEntity<T extends FieldValue | Timestamp> {
  id: string
  userId: string
  created_at: T
  updated_at: T | null
  email: string
  emailVerified: boolean
  displayName: string
  likeCount: number
  reportsReceived: number
  appealStatus: 'none' | 'pending' | 'approved' | 'rejected'
  fcmToken: string | null
  notificationPreferences: {
    postLikes: boolean
    commentLikes: boolean
    replyLikes: boolean
    comments: boolean
    replies: boolean
    answers: boolean
    reports: boolean
  }
  accountStatus: 'active' | 'restricted' | 'pending'
}

export type UserDataWrite = UserEntity<FieldValue>
export type UserDataRead = UserEntity<Timestamp>

// API Response Base
interface ApiBaseEntity {
  id: string
  userId: string
  created_at: string
  updated_at: string | null
  isVisible: boolean
}

interface ContentEntity<T extends FieldValue | Timestamp> extends BaseEntity<T> {
  content: string
  authorType: AuthorType
  authorName: string
  likeCount: number
  isLikedByCurrentUser: boolean
  reportsReceived: number
  appealStatus: 'none' | 'pending' | 'approved' | 'rejected'
}

export interface ReportEntity<T extends FieldValue | Timestamp> {
  postOwnerId?: string
  id: string
  userId: string
  contentRef: DocumentReference
  contentType: ReportableEntity
  contentId: string
  created_at: T
  updated_at: T | null
  reason: ReportReason
  reasonDetail?: string | null
  status: 'pending' | 'resolved'
  actionTaken: 'contentHidden' | 'userRestricted' | 'dismissed' | null
  actionDetails: string | null
}

export type ReportDataWrite = ReportEntity<FieldValue>
export type ReportDataRead = ReportEntity<Timestamp>

export interface AppealEntity<T extends FieldValue | Timestamp> {
  id: string
  userId: string
  contentRef: DocumentReference
  contentType: ReportableEntity
  contentId: string
  created_at: T
  updated_at: T | null
  reason: AppealReason
  reasonDetail?: string | null
  status: 'pending' | 'approved' | 'rejected'
  statusDetails: string | null
  rejectedAt: T | null
}

export type AppealDataWrite = AppealEntity<FieldValue>
export type AppealDataRead = AppealEntity<Timestamp>

// User Types
export interface UserDetailResponse {
  id: string
  userId: string
  created_at: string
  updated_at: string | null
  email: string
  emailVerified: boolean
  displayName: string
  likeCount: number
  reportsReceived: number
  appealStatus: 'none' | 'pending' | 'approved' | 'rejected'
  fcmToken: string | null
  notificationPreferences: {
    postLikes: boolean
    commentLikes: boolean
    replyLikes: boolean
    comments: boolean
    replies: boolean
    answers: boolean
    reports: boolean
  }
  accountStatus: 'active' | 'restricted' | 'pending'
}

// Job Types
export interface JobSearchParams {
  authKey: string // 인증키
  callTp: string // 호출 유형: 'L' (목록), 'D' (상세)
  returnType: string // 반환 형식: 'xml'
  startPage: number // 검색 시작 위치 (기본값 1, 최대 1000)
  display: number // 출력 건수 (기본값 10, 최대 100)
  region?: string // 근무지역 (다중검색 가능)
  occupation?: string // 직종 (다중검색 가능)
  salTp?: string // 임금형태 (D: 일급, H: 시급, M: 월급, Y: 연봉)
  minPay?: number // 최소급여
  maxPay?: number // 최대급여
  education?: string // 학력 (00: 학력무관, 01: 초졸이하, 등)
  career?: string // 경력 (N: 신입, E: 경력, Z: 관계없음)
  minCareerM?: number // 최소 경력 개월
  maxCareerM?: number // 최대 경력 개월
  pref?: string // 우대조건 (Y: 장애인 병행채용, D: 장애인만 채용)
  subway?: string // 역세권 (다중검색 가능)
  empTp?: string // 고용형태 (4: 파견근로, 10: 무기계약, 등)
  termContractMmcnt?: string // 근무기간 (1: 1~3개월, 등)
  holidayTp?: string // 근무형태 (1: 주 5일, 2: 주 6일, 등)
  coTp?: string // 기업형태 (01: 대기업, 03: 벤처기업, 등)
  busino?: string // 사업자등록번호
  dtlSmlgntYn?: string // 강소기업 여부 (Y)
  workStudyJoinYn?: string // 일학습병행 여부 (Y)
  smlgntCoClcd?: string // 강소기업 분류코드
  workerCnt?: string // 사원수 (W5: 5인 미만, 등)
  welfare?: string // 근무편의 (01: 기숙사, 등)
  certLic?: string // 자격면허 (다중검색 가능)
  regDate?: string // 등록일 (D-0: 오늘, M-1: 한달, 등)
  keyword?: string // 키워드 (다중검색 가능, UTF-8)
  untilEmpWantedYn?: string // 채용시까지 구인 여부 (Y/N)
  minWantedAuthDt?: string // 최소 구인인증일자
  maxWantedAuthDt?: string // 최대 구인인증일자
  empTpGb?: string // 채용구분 (1: 상용직, 2: 일용직)
  sortOrderBy?: string // 정렬방식 (DESC: 상향, ASC: 하향)
  major?: string // 전공 (다중검색 가능)
  foreignLanguage?: string // 외국어 (다중검색 가능)
  comPreferential?: string // 컴퓨터 우대조건 (1: 문서작성, 등)
  pfPreferential?: string // 일반 우대조건 (B: 50세 이상 고령자, 등)
  workHrCd?: string // 근무시간 (1: 오전, 9: 종일, 등)
}

export interface JobPosting {
  wantedAuthNo: string // 구인인증번호
  company: string // 회사명
  busino?: string // 사업자등록번호
  indTpNm?: string // 업종
  title: string // 채용제목
  salTpNm?: string // 임금형태
  sal?: string // 급여
  minSal?: string // 최소임금액
  maxSal?: string // 최대임금액
  region: string // 근무지역
  holidayTpNm?: string // 근무형태
  minEdubg?: string // 최소학력
  maxEdubg?: string // 최대학력
  career?: string // 경력
  regDt?: string // 등록일자
  closeDt?: string // 마감일자
  infoSvc?: string // 정보제공처
  wantedInfoUrl?: string // 채용정보 URL
  wantedMobileInfoUrl?: string // 모바일 채용정보 URL
  zipCd?: string // 우편주소
  strtnmCd?: string // 도로명주소
  basicAddr?: string // 기본주소
  detailAddr?: string // 상세주소
  empTpCd?: string // 고용형태코드
  jobsCd?: string // 직종코드
  smodifyDtm?: string // 최종수정일
}

export interface WorkNetApiResponse {
  total: number // 총 건수
  startPage: number // 시작 페이지
  display: number // 출력 건수
  wanted: JobPosting[] // 채용 공고 목록
}

export interface UserJobPreference {
  field: keyof JobSearchParams | keyof JobPosting
  priority: number // 1이 가장 중요함 (숫자가 클수록 덜 중요)
  expectedValue?: string | number | boolean // 사용자가 원하는 값
  preferenceWeight?: number // AI가 참고할 수 있는 가중치 (optional)
  excludeIfNotMatched?: boolean // 필수 조건 여부 (true면 매칭 안되면 추천 제외)
}

export interface UserJobInteraction<T extends FieldValue | Timestamp> extends BaseEntity<T> {
  jobId: string // 채용 공고 ID
  isFavorite: boolean // 관심 채용 공고 여부
  note?: string | null // 사용자 메모
}

export type UserJobInteractionWrite = UserJobInteraction<FieldValue>
export type UserJobInteractionRead = UserJobInteraction<Timestamp>

export interface UserJobRecommendation<T extends FieldValue | Timestamp> extends BaseEntity<T> {
  jobId: string // 추천된 채용 공고 ID
  recommendationReason: string // 자연어로 된 추천 사유 (ex. "경력 조건이 적합함", "지역이 선호지역과 일치")
  score?: number // 추천 점수 (0 ~ 1 사이, 혹은 0 ~ 100점 척도)
  matchedFields?: string[] // AI가 일치한다고 판단한 필드 목록 (ex. ['region', 'career', 'salary'])
  source: 'ai' | 'manual' | 'hybrid' // 추천 생성 방식
  createdAt: T // 채용 공고 생성시간
  jobSnapshot?: Partial<JobPosting> // 추천 당시 JobPosting의 주요 정보 스냅샷 (optional)
  interactionRef?: DocumentReference // 사용자의 UserJobInteraction 문서 참조 (있을 경우)
  modelVersion?: string // 사용된 AI 모델 또는 로직 버전 (ex. 'v1.2')
  isAcknowledged: boolean // 사용자가 이 추천을 확인했는지 여부
  acknowledgedAt?: T | null // 확인 시간
  expiresAt?: T | null // 이 추천의 유효기간 (예: 채용공고 마감일 이후로 자동 만료)
}

export type UserJobRecommendationWrite = UserJobRecommendation<FieldValue>
export type UserJobRecommendationRead = UserJobRecommendation<Timestamp>

// Post Types
export interface PostDataWrite extends ContentEntity<FieldValue> {
  title: string
  imageFilePaths: string[]
  commentCount: number
}

export interface PostDataRead extends ContentEntity<Timestamp> {
  title: string
  imageFilePaths: string[]
  commentCount: number
  comments?: CommentDataRead[] | null
}

export type PostUpdateFields = Partial<Omit<PostDataWrite, 'created_at'>>

export interface CreatePostRequest {
  title: string
  content: string
  authorType: AuthorType
  authorName: string
}

export interface UpdatePostRequest {
  postOwnerId: string
  postId: string
  title?: string
  content?: string
  authorType?: AuthorType
  authorName?: string
  isVisible?: boolean
}

export interface DeletePostRequest {
  postOwnerId: string
  postId: string
}

export interface PostPreviewResponse extends ApiBaseEntity {
  title: string
  content: string
  authorType: AuthorType
  authorName: string
  commentCount: number
  likeCount: number
  reportsReceived: number
  appealStatus: 'none' | 'pending' | 'approved' | 'rejected'
  isLikedByCurrentUser: boolean
  path: string
}

export interface PostDetailResponse extends ApiBaseEntity {
  title: string
  content: string
  imageFilePaths: string[]
  authorType: AuthorType
  authorName: string
  commentCount: number
  likeCount: number
  reportsReceived: number
  appealStatus: 'none' | 'pending' | 'approved' | 'rejected'
  isLikedByCurrentUser: boolean
  comments?: CommentDetailResponse[]
}

// Comment Types
export interface CommentDataWrite extends ContentEntity<FieldValue> {
  postId: string
  postOwnerId: string
  replyCount: number
}

export interface CommentDataRead extends ContentEntity<Timestamp> {
  postId: string
  postOwnerId: string
  replyCount: number
  replies?: ReplyDataRead[] | null
}

export type CommentUpdateFields = Partial<Omit<CommentDataWrite, 'created_at'>>

export interface AddCommentRequest {
  postOwnerId: string
  postId: string
  content: string
  authorType: AuthorType
  authorName: string
}

export interface UpdateCommentRequest {
  postOwnerId: string
  postId: string
  commentId: string
  content?: string
  authorType?: AuthorType
  authorName?: string
  isVisible?: boolean
}

export interface DeleteCommentRequest {
  postOwnerId: string
  postId: string
  commentId: string
}

export interface CommentPreviewResponse extends ApiBaseEntity {
  content: string
  authorType: AuthorType
  authorName: string
  postOwnerId: string
  postId: string
  replyCount: number
  likeCount: number
  reportsReceived: number
  appealStatus: 'none' | 'pending' | 'approved' | 'rejected'
  isLikedByCurrentUser: boolean
  path: string
}

export interface CommentDetailResponse extends ApiBaseEntity {
  postId: string
  postOwnerId: string
  content: string
  authorType: AuthorType
  authorName: string
  replyCount: number
  likeCount: number
  reportsReceived: number
  appealStatus: 'none' | 'pending' | 'approved' | 'rejected'
  isLikedByCurrentUser: boolean
  replies?: ReplyDetailResponse[]
}

// Reply Types
export interface ReplyDataWrite extends ContentEntity<FieldValue> {
  postId: string
  postOwnerId: string
  commentId: string
}

export interface ReplyDataRead extends ContentEntity<Timestamp> {
  postId: string
  postOwnerId: string
  commentId: string
}

export type ReplyUpdateFields = Partial<Omit<ReplyDataWrite, 'created_at'>>

export interface AddReplyRequest {
  postOwnerId: string
  postId: string
  commentId: string
  content: string
  authorType: AuthorType
  authorName: string
}

export interface UpdateReplyRequest {
  postOwnerId: string
  postId: string
  commentId: string
  replyId: string
  content?: string
  authorType?: AuthorType
  authorName?: string
  isVisible?: boolean
}

export interface DeleteReplyRequest {
  postOwnerId: string
  postId: string
  commentId: string
  replyId: string
}

export interface ReplyPreviewResponse extends ApiBaseEntity {
  content: string
  authorType: AuthorType
  authorName: string
  postOwnerId: string
  postId: string
  commentId: string
  likeCount: number
  reportsReceived: number
  appealStatus: 'none' | 'pending' | 'approved' | 'rejected'
  isLikedByCurrentUser: boolean
  path: string
}

export interface ReplyDetailResponse extends ApiBaseEntity {
  postId: string
  postOwnerId: string
  commentId: string
  content: string
  authorType: AuthorType
  authorName: string
  likeCount: number
  reportsReceived: number
  appealStatus: 'none' | 'pending' | 'approved' | 'rejected'
  isLikedByCurrentUser: boolean
}

// Question Types
export interface QuestionDataWrite extends BaseEntityWrite {
  title: string
  content: string
  userName: string
  isPrivate: boolean
  answerCount: number
}

export interface QuestionDataRead extends BaseEntityRead {
  title: string
  content: string
  userName: string
  isPrivate: boolean
  answerCount: number
}

export type QuestionUpdateFields = Partial<Omit<QuestionDataWrite, 'created_at'>>

export interface CreateQuestionRequest {
  title: string
  content: string
  userName: string
  isPrivate?: boolean
}

export interface UpdateQuestionRequest {
  questionOwnerId: string
  questionId: string
  title?: string
  content?: string
  userName?: string
  isPrivate?: boolean
}

export interface DeleteQuestionRequest {
  questionOwnerId: string
  questionId: string
  isPrivate?: boolean
}

export interface QuestionPreviewResponse extends ApiBaseEntity {
  title: string
  content: string
  userName: string
  isPrivate: boolean
  answerCount: number
  path: string
}

export interface QuestionDetailResponse extends ApiBaseEntity {
  title: string
  content: string
  userName: string
  isPrivate: boolean
  answerCount: number
  answers?: AnswerDetailResponse[]
}

// Answer Types
export interface AnswerDataWrite extends BaseEntityWrite {
  questionId: string
  questionOwnerId: string
  content: string
  userName: string
  isPrivate: boolean
}

export interface AnswerDataRead extends BaseEntityRead {
  questionId: string
  questionOwnerId: string
  content: string
  userName: string
  isPrivate: boolean
}

export type AnswerUpdateFields = Partial<Omit<AnswerDataWrite, 'created_at'>>

export interface AddAnswerRequest {
  questionOwnerId: string
  questionId: string
  content: string
  userName: string
  isPrivate?: boolean
}

export interface UpdateAnswerRequest {
  questionOwnerId: string
  questionId: string
  answerId: string
  content?: string
  userName?: string
  isPrivate?: boolean
}

export interface DeleteAnswerRequest {
  questionOwnerId: string
  questionId: string
  answerId: string
  isPrivate?: boolean
}

export interface AnswerPreviewResponse extends ApiBaseEntity {
  questionId: string
  questionOwnerId: string
  content: string
  userName: string
  isPrivate: boolean
  path: string
}

export interface AnswerDetailResponse extends ApiBaseEntity {
  questionId: string
  questionOwnerId: string
  content: string
  userName: string
  isPrivate: boolean
}

// Like Types
export interface TogglePostLikeRequest {
  postOwnerId: string
  postId: string
}

export interface ToggleCommentLikeRequest {
  postOwnerId: string
  postId: string
  commentId: string
}

export interface ToggleReplyLikeRequest {
  postOwnerId: string
  postId: string
  commentId: string
  replyId: string
}

export interface ToggleLikeResponse {
  success: boolean
  isLiked: boolean
  likeCount: number
  updatedAt: string | null
}

// Report Types
export interface UserReportRequest {
  contentType: ReportableEntity
  reportedUserId: string
  reporterId: string
  reason: ReportReason
  reasonDetail?: string | null
}

export interface PostReportRequest {
  postOwnerId: string
  contentType: ReportableEntity
  postId: string
  reporterId: string
  reason: ReportReason
  reasonDetail?: string | null
}

export interface CommentReportRequest {
  postOwnerId: string
  contentType: ReportableEntity
  commentId: string
  postId: string
  reporterId: string
  reason: ReportReason
  reasonDetail?: string | null
}

export interface ReplyReportRequest {
  postOwnerId: string
  contentType: ReportableEntity
  replyId: string
  commentId: string
  postId: string
  reporterId: string
  reason: ReportReason
  reasonDetail?: string | null
}

export interface ReportDetailResponse {
  id: string
  userId: string
  created_at: string
  updated_at: string | null
  contentRef: string
  contentType: ReportableEntity
  contentId: string
  reason: ReportReason
  reasonDetail?: string | null
  status: 'pending' | 'resolved'
  actionTaken: 'contentHidden' | 'userRestricted' | 'dismissed' | null
  actionDetails: string | null
  path: string
}

// Appeal Types
export interface UserAppealRequest {
  userId: string
  reason: AppealReason
  reasonDetail?: string | null
}

export interface PostAppealRequest {
  postOwnerId: string
  contentType: ReportableEntity
  postId: string
  reason: AppealReason
  reasonDetail?: string | null
}

export interface CommentAppealRequest {
  postOwnerId: string
  contentType: ReportableEntity
  commentId: string
  postId: string
  reason: AppealReason
  reasonDetail?: string | null
}

export interface ReplyAppealRequest {
  postOwnerId: string
  contentType: ReportableEntity
  replyId: string
  commentId: string
  postId: string
  reason: AppealReason
  reasonDetail?: string | null
}

export interface AppealDetailResponse {
  id: string
  userId: string
  created_at: string
  updated_at: string | null
  contentRef: string
  contentType: ReportableEntity
  contentId: string
  reason: AppealReason
  reasonDetail?: string | null
  status: 'pending' | 'approved' | 'rejected'
  statusDetails: string | null
  rejectedAt?: string | null
}

// Notification Types
export interface FcmNotificationPayload {
  title: string
  body: string
  data: Record<string, string>
}
